<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  bot: { type: Object, required: true },
});

const emit = defineEmits(['changed']);

const store = useStore();
const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const agentBots = useMapGetter('agentBots/getBots');

const saving = ref({});

const botByInbox = computed(() => {
  const map = {};
  agentBots.value.forEach(bot => {
    (bot.inboxes || []).forEach(inbox => {
      map[inbox.id] = bot;
    });
  });
  return map;
});

// One inbox holds one bot. An inbox already answered by another bot is shown with its name rather
// than hidden: that is the conflict somebody has to resolve to move the bot here.
const rows = computed(() =>
  inboxes.value.map(inbox => {
    const owner = botByInbox.value[inbox.id];
    const isMine = owner?.id === props.bot.id;
    return {
      id: inbox.id,
      name: inbox.name,
      channel: inbox.channel_type,
      isMine,
      takenBy: !isMine && owner ? owner.name : '',
    };
  })
);

const toggleInbox = async row => {
  saving.value[row.id] = true;
  try {
    if (row.isMine) {
      await store.dispatch('agentBots/disconnectBot', { inboxId: row.id });
      useAlert(t('AGENT_BOTS.CHANNELS.DISCONNECTED'));
    } else {
      await store.dispatch('agentBots/setAgentBotInbox', {
        inboxId: row.id,
        botId: props.bot.id,
      });
      useAlert(t('AGENT_BOTS.CHANNELS.CONNECTED'));
    }
    await store.dispatch('agentBots/get');
    emit('changed');
  } catch (error) {
    useAlert(t('AGENT_BOTS.CHANNELS.ERROR'));
  } finally {
    saving.value[row.id] = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ $t('AGENT_BOTS.CHANNELS.DESCRIPTION') }}
    </p>

    <div
      v-for="row in rows"
      :key="row.id"
      class="flex items-center gap-4 p-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
    >
      <div class="flex flex-col flex-grow min-w-0">
        <span class="text-sm font-medium text-n-slate-12">{{ row.name }}</span>
        <span
          class="text-sm"
          :class="row.takenBy ? 'text-n-amber-11' : 'text-n-slate-11'"
        >
          {{
            row.takenBy
              ? $t('AGENT_BOTS.CHANNELS.TAKEN_BY', { name: row.takenBy })
              : row.channel
          }}
        </span>
      </div>
      <Switch
        :model-value="row.isMine"
        :disabled="Boolean(row.takenBy) || saving[row.id]"
        @change="toggleInbox(row)"
      />
    </div>
  </div>
</template>
